import dayjs from 'dayjs';
import calender from 'dayjs/plugin/calendar';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import getSymbolFromCurrency from 'currency-symbol-map';

import { Claims } from '../types/database';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calender);

export const DEFAULT_STORE_CREDIT_MAX_PERCENTAGE = 100;

export const StateToText: { [key: string]: string } = {
  APPROVED: 'Approved',
  ARCHIVED: 'Archived',
  CLOSED: 'Closed',
  COMMUNICATING: 'Communicating',
  DENIED: 'Denied',
  DISMISSED: 'Pending',
  NEW: 'To Do',
  OPEN: 'On Hold',
  RESOLVED: 'Resolved',
  RESOLVED_BY_BRAND: 'Resolved by Brand',
  TEMPORARILY_DISMISSED: 'Temporarily Dismissed',
  WAITING_FOR_BRAND: 'Waiting for Brand',
  WAITING_FOR_CUSTOMER: 'Waiting for Customer',
  WAITING_FOR_OP: 'Waiting for OP',
  DRAFT: 'Pending Customer',
};

/**
 * Look up creditStore for a (claimType, policyType) pair against the merchant's coverage matrix.
 * Falls back to the legacy creditedMap when no matching rule exists (e.g. store hasn't configured
 * the matrix). Use this in place of `creditedMap[claimType]` whenever rules + policyType are
 * available; the matrix is the source of truth and overrides the legacy default.
 */
export function getCreditStoreFromMatrix(
  rules: { claimType: string; category: string; creditStore: boolean }[] | undefined | null,
  claimType: string,
  policyType: string | undefined | null,
): boolean {
  if (rules && policyType) {
    const rule = rules.find(r => r.claimType === claimType && r.category === policyType);
    if (rule) return rule.creditStore;
  }
  if (claimType in creditedMap) return creditedMap[claimType];
  // eslint-disable-next-line no-console
  console.warn(
    `getCreditStoreFromMatrix: no rule + no static default for claimType="${claimType}", policyType="${policyType}" — defaulting to false`,
  );
  return false;
}

export const creditedMap: { [key: string]: boolean } = {
  CARRIER_DID_NOT_RECEIVE: false,
  DAMAGED: true,
  DEFECTIVE_ITEM: false,
  LATE_SHIPPING: false,
  LOST: true,
  MISSING_ITEM: false,
  ORDER_DELIVERED_NOT_RECEIVED: true,
  TRACKING_NOT_MOVING: true,
  PRODUCT_NOT_AS_DESCRIBED: false,
  RETURNED_TO_SENDER: false,
  STOLEN: true,
  WRONG_ADDRESS_CARRIER: true,
  WRONG_ADDRESS_CUSTOMER: false,
  WRONG_ITEM: false,
  WRONG_ADDRESS: false,
};

/**
 * Whether a claim type's workflow needs carrier proof of delivery. Mirrors the POD checks in
 * brand-ai-agents (`global/workflows/dnr.md`, `stolen.md`, `wrong-address.md`, all via the shared
 * "Proof of Delivery (POD) Check"); every other type only needs shipment status. `MULTIPLE` is
 * composite and may contain a POD-requiring sub-claim, so it counts as requiring POD.
 *
 * Exhaustive over Claims.ClaimType on purpose — a new claim type won't compile until someone
 * decides which side it falls on.
 */
const POD_REQUIRED_BY_CLAIM_TYPE: Record<Claims.ClaimType, boolean> = {
  [Claims.ClaimType.STOLEN]: true,
  [Claims.ClaimType.ORDER_DELIVERED_NOT_RECEIVED]: true,
  [Claims.ClaimType.WRONG_ADDRESS]: true,
  [Claims.ClaimType.MULTIPLE]: true,
  [Claims.ClaimType.DAMAGED]: false,
  [Claims.ClaimType.DEFECTIVE_ITEM]: false,
  [Claims.ClaimType.WRONG_ITEM]: false,
  [Claims.ClaimType.MISSING_ITEM]: false,
  [Claims.ClaimType.RETURNED_TO_SENDER]: false,
  [Claims.ClaimType.TRACKING_NOT_MOVING]: false,
};

/**
 * Defaults to true for an absent or unrecognized claim type: callers use this to pick which
 * AfterShip account a tracking is registered under, and losing POD on a claim that needed it is
 * far worse than registering a tracking-only claim on the POD-capable account.
 */
export function requiresProofOfDelivery(claimType: Claims.ClaimType | string | null | undefined): boolean {
  if (!claimType) return true;
  return POD_REQUIRED_BY_CLAIM_TYPE[claimType as Claims.ClaimType] ?? true;
}

export const ClaimTypeToText: { [key: string]: string } = {
  CARRIER_DID_NOT_RECEIVE: 'Carrier Did Not Receive',
  DAMAGED: 'Damaged',
  DEFECTIVE_ITEM: 'Defective Item',
  LATE_SHIPPING: 'Late Shipping',
  LOST: 'Lost',
  MISSING_ITEM: 'Missing Item',
  ORDER_DELIVERED_NOT_RECEIVED: 'Order Delivered Not Received',
  PRODUCT_NOT_AS_DESCRIBED: 'Product Not As Described',
  RETURNED_TO_SENDER: 'Returned to Sender',
  TRACKING_NOT_MOVING: 'Tracking Not Moving',
  STOLEN: 'Stolen',
  WRONG_ADDRESS_CARRIER: 'Wrong Address Carrier',
  WRONG_ADDRESS_CUSTOMER: 'Wrong Address Customer',
  WRONG_ITEM: 'Wrong Item',
  MULTIPLE: 'Multiple',
  WRONG_ADDRESS: 'Wrong Address',
};

export const PricingModelToText: { [key: string]: string } = {
  COST_PER_ORDER: 'Cost Per Order',
  COST_PER_ORDER_ITEM: 'Cost Per Order Item',
  COST_PER_RESOLUTION: 'Cost Per Resolution',
};

export const ClaimCategoryToText: { [key: string]: string } = {
  WARRANTY: 'Warranty',
  SHIPPING: 'Shipping',
};

// Parent ClaimType values that can have merchant-defined sub-types per ClaimCategory.
// Mirrors the backend allow-list in apps/claims-svc/src/claims/subtype-category.ts.
export const SUBTYPE_PARENT_TYPES: Record<Claims.ClaimCategory, Claims.ClaimType[]> = {
  [Claims.ClaimCategory.WARRANTY]: [Claims.ClaimType.DAMAGED, Claims.ClaimType.DEFECTIVE_ITEM],
  [Claims.ClaimCategory.SHIPPING]: [
    Claims.ClaimType.DAMAGED,
    Claims.ClaimType.STOLEN,
    Claims.ClaimType.WRONG_ITEM,
    Claims.ClaimType.ORDER_DELIVERED_NOT_RECEIVED,
    Claims.ClaimType.MISSING_ITEM,
    Claims.ClaimType.RETURNED_TO_SENDER,
    Claims.ClaimType.TRACKING_NOT_MOVING,
    Claims.ClaimType.WRONG_ADDRESS,
    Claims.ClaimType.DEFECTIVE_ITEM,
  ],
};

export const SalesChannelToText: { [key: string]: string } = {
  IN_STORE: 'In Store',
  ONLINE: 'Online',
};

export const FulfillmentStatusToText: { [key: string]: string } = {
  AWAITING_SHIPMENT: 'Awaiting Shipment',
  DELIVERED: 'Delivered',
  IN_TRANSIT: 'In Transit',
  FULFILLED: 'Fulfilled',
  ON_HOLD: 'On Hold',
  PARTIALLY_FULFILLED: 'Partially Fulfilled',
  OUT_FOR_DELIVERY: 'Out For Delivery',
  ATTEMPTED_DELIVERY: 'Attempted Delivery',
  NOT_DELIVERED: 'Not Delivered',
  LABEL_PURCHASED: 'Label Purchased',
  LABEL_PRINTED: 'Label Printed',
  READY_FOR_PICKUP: 'Ready For Pickup',
  CONFIRMED: 'Confirmed',
  SCHEDULED: 'Scheduled',
  UNFULFILLED: 'Unfulfilled',
};

export const ResolutionTypeToText: { [key: string]: string } = {
  ADDRESS_TYPO: 'Customer Shipping Address Typo',
  CUSTOM: 'Custom',
  CUSTOMER_RESPONDED: 'Customer Responded',
  EXPIRED30: 'Claim Timeframe Expired',
  FOLLOW_UP: 'Follow up',
  INSUFFICIENT_SHIPPER: 'Insufficient Shipper Packaging',
  NOT_COVERED: 'Claim Type Not Covered',
  NO_RESPONSE: 'No Response',
  OTHER: 'Other',
  PACKAGE_ARRIVED: 'Package Arrived',
  PHOTO_VERIFIED: 'Delivery Photo Verified',
  REFUND: 'Refund',
  RESHIP: 'Reship',
  STORE_CREDIT: 'Store Credit',
  RETURN_TO_SENDER: 'Return To Sender',
  THRESHOLD_REACHED: 'Excessive Claim Threshold Reached',
  ALREADY_REFUNDED: 'Already Refunded',
  ALREADY_REPLACED: 'Already Replaced',
  POTENTIAL_FRAUD: 'Potential Fraud',
  NOT_MERCHANT_PRODUCT: 'Not sold by this store',
  FLAT_RATE: 'Flat Rate',
  FLAT_RATE_STORE_CREDIT: 'Flat Rate Store Credit',
};

export function getCustomerResolutionTypeText(key: string) {
  const customerOverride: { [key: string]: string } = {
    POTENTIAL_FRAUD: 'Not Covered',
  };

  return customerOverride[key] || ResolutionTypeToText[key] || key;
}

export const StoreCreditToText: { [key: string]: string } = {
  STORE_CREDIT: 'Store Credit',
  GIFT_CARD: 'Gift Card',
  DISCOUNT_CODE: 'Discount Code',
  ACCOUNT_CREDIT: 'Account Credit',
};

export const OriginationToText: { [key: string]: string } = {
  ORDER: 'Order',
  CLAIM: 'Claim',
};

export function formatPrice(amount: number, currency: string) {
  const formatter = new Intl.NumberFormat(undefined, {
    currency,
    style: 'currency',
  });

  return formatter.format(amount);
}

export function formatPriceCurrency(amount: number, currency: string) {
  const formatter = new Intl.NumberFormat('en-US', {
    currency,
    style: 'currency',
  });

  return formatter.format(amount);
}

export function getCurrencySymbol(currency: string) {
  const number = new Intl.NumberFormat('en-US', {
    currency,
    style: 'currency',
  }).formatToParts(1);

  const currencyPart = number.find(part => part.type === 'currency');
  return currencyPart ? currencyPart.value : '$';
}

export function formatCurrency(amount: number, currency: string) {
  const symbol = getSymbolFromCurrency(currency);
  const value = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (symbol === currency || !symbol) return `${currency} ${value}`;
  return `${symbol}${value} ${currency}`;
}

export function getProtectionStatus(order: any) {
  const eligibleForOta =
    (order.policyId && order.eligibleForOta === 'IS_OTA') || (!order.policyId && order.eligibleForOta === 'ELIGIBLE');

  const statusMap: Record<'ota' | 'protected' | 'removed' | 'unprotected', boolean> = {
    ota: eligibleForOta && !order.sourceCancelledAt,
    protected: !!order.policyId && !order.sourceCancelledAt && !order.policyCancelledAt,
    removed: !!order.sourceCancelledAt || !!order.policyCancelledAt,
    unprotected: !order.policyId && !order.sourceCancelledAt && !order.policyCancelledAt,
  };

  for (const status in statusMap) {
    if (statusMap[status as keyof typeof statusMap]) {
      return status;
    }
  }

  return null;
}

export enum DateFormat {
  MonthDayYearTime = 'MMM D YYYY, hh:mm A',
  DayMonthYearTime = 'DD/MM/YYYY hh:mm A',
}

// TODO: Allow multiple formats to be passed using the format option
export const formatDateV2 = (
  date: string,
  opts: {
    timezone?: string;
    format?: DateFormat;
  },
) => {
  const transformedDate = opts.timezone ? dayjs.tz(date, opts.timezone) : dayjs(date);

  return transformedDate.format(opts.format ?? DateFormat.MonthDayYearTime);
};

// @deprecated use the formatDateV2 function
export const formatDate = (date: string | Date | number, timezone?: string): string => {
  const inputDate = timezone ? dayjs.tz(date, timezone) : dayjs(date);

  return inputDate.calendar(null, {
    lastWeek: 'MMM D YYYY [at] h:mm A',
    sameElse: 'MMM D YYYY [at] h:mm A',
  });
};

// @deprecated use the formatDateV2 function
export const formatDate2 = (date: string | Date | number, format = 'DD/MM/YYYY h:m A') => {
  return dayjs(date).format(format);
};

export const claimStateMapper = {
  state: (claimState?: string) => {
    const mapper: Record<string, string> = {
      'on-hold': 'open',
      onhold: 'open',
      todo: 'new',
      pending: 'dismissed',
    };

    return mapper[claimState?.toLowerCase() ?? ''] ?? claimState;
  },
  label: (label?: string): string => {
    const mapper: Record<string, string> = {
      new: 'To Do',
      open: 'On Hold',
      dismissed: 'Pending',
      reopened: 'Re-Opened',
    };

    return mapper[label?.toLowerCase() ?? ''] ?? label;
  },
};
